import Skeleton from '@mui/material/Skeleton';
import moment from 'moment';
import { type JSX, useMemo, useState } from 'react';

import { GroupedBarsChart } from 'Components/BaseCharts/GroupedBarsChart';
import { ChartAxis } from 'Components/BaseCharts/charts.common';
import { FormFieldDateOptionSelect } from 'Components/FormField/FormFieldDateOptionSelect/FormFieldDateOptionSelect';
import { ChangeEventType } from 'Components/FormField/FormFieldSelect/FormFieldSelect';
import { FormFieldSelectDark } from 'Components/FormField/FormFieldSelectDark/FormFieldSelectDark';
import { CustomDateRangeModal } from 'Components/Modal/CustomDateRangeModal/CustomDateRangeModal';
import { Text } from 'Components/Text/Text';
import { useDateFilter } from 'Hooks/DateFilter';
import { DashboardRiskOptions, DateOptionSelectOption, DefaultUpcomingDateRangeOptions, DueDiligenceDashboardAssessmentSubject, DueDiligenceDashboardRiskType, isValidDashboardRiskType } from 'Models/Dashboards';
import { Effectiveness, effectivenessAsString } from 'Models/OperationalControls';
import { DueDiligenceModule, ProductModule } from 'Models/ProductModule';
import { RiskRating, riskRatingAsString } from 'Models/RiskRating';
import { getMonths } from 'Pages/Dashboards/Helpers/Dashboards.helpers';

import { AssessmentSubjectsDisplayModal, SelectedAssessmentSubjectDetails } from '../AssessmentSubjectsDisplayModal/AssessmentSubjectsDisplayModal';
import { AssessmentsBarChartDataRow, buildAssessmentsBarChartByMonth } from '../DueDiligenceDashboard.helpers';
import styles from '../DueDiligenceDashboard.module.css';

enum Modals {
    NONE,
    DISPLAY_ASSESSMENT_SUBJECTS,
    CUSTOM_DATE,
}

interface AssessmentSubjectDataItem {
    monthKey: string;
    riskScore: number | undefined;
    assessmentSubjectId: string;
    assessmentSubjectName: string;
    organizationId: string;
    organizationName: string;
}

interface UpcomingAssessmentsChartData {
    assessmentDataRows: AssessmentsBarChartDataRow[];
    xAxisLabels: string[];
    yAxisInfo: ChartAxis[];
    assessmentSubjectsData: AssessmentSubjectDataItem[];
}

interface UpcomingAssessmentsChartProps {
    module: DueDiligenceModule;

    assessmentSubjects?: DueDiligenceDashboardAssessmentSubject[];
    assessmentSubjectsError?: string;
}

export const UpcomingAssessmentsChart = (props: UpcomingAssessmentsChartProps): JSX.Element => {
    const [modalState, setModalState] = useState<Modals>(Modals.NONE);
    const [assessmentSubjectsModalSubtitle, setAssessmentSubjectsModalSubtitle] = useState<string>();
    const [selectedAssessmentSubjects, setSelectedAssessmentSubjects] = useState<SelectedAssessmentSubjectDetails[]>();
    const [riskType, setRiskType] = useState<DueDiligenceDashboardRiskType>(DueDiligenceDashboardRiskType.RESIDUAL);
    const dateFilterHookValues = useDateFilter(DateOptionSelectOption.NEXT_THREE_MONTHS);

    const upcomingAssessmentsChartData = useMemo<UpcomingAssessmentsChartData | undefined>(() => {
        if (props.assessmentSubjects) {
            const upcomingAssessmentsData: AssessmentSubjectDataItem[] = props.assessmentSubjects
                .filter((assessmentSubject) => assessmentSubject.assessment_workflow_setup.due_date !== undefined)
                .map((assessmentSubject) => ({
                    monthKey: assessmentSubject.is_overdue ? ('overdue' as const) : moment(assessmentSubject.assessment_workflow_setup.due_date).format('MMM YY'),
                    riskScore: (() => {
                        switch (riskType) {
                            case DueDiligenceDashboardRiskType.INHERENT:
                                return assessmentSubject.assessment_workflow_data.irq_inherent_risk_score ?? RiskRating.UNRATED;
                            case DueDiligenceDashboardRiskType.CONTROL_EFFECTIVENESS:
                                return assessmentSubject.assessment_workflow_data.final_review_human_assessment.effectiveness ?? Effectiveness.UNRATED;
                            case DueDiligenceDashboardRiskType.RESIDUAL:
                                return assessmentSubject.assessment_workflow_data.final_review_human_assessment.residual_risk ?? RiskRating.UNRATED;
                        }
                    })(),
                    assessmentSubjectId: assessmentSubject.id,
                    assessmentSubjectName: assessmentSubject.name,
                    organizationId: assessmentSubject.vendor_id,
                    organizationName: assessmentSubject.vendor_name,
                }));

            const baseMonths = getMonths(dateFilterHookValues.selection.dateRange.startDate, dateFilterHookValues.selection.dateRange.endDate);
            const months = ['overdue' as const, ...baseMonths];

            const assessmentDataRows = buildAssessmentsBarChartByMonth(upcomingAssessmentsData, riskType, months);
            const yAxisInfo: ChartAxis[] = [{ name: props.module === ProductModule.UCRM ? 'Number of Use Cases' : 'Number of Services', position: 'left', minInterval: 1 }];

            return { assessmentDataRows, xAxisLabels: months, yAxisInfo, assessmentSubjectsData: upcomingAssessmentsData };
        }
    }, [props.assessmentSubjects, props.module, riskType, dateFilterHookValues]);

    const handleChartClick = (item: string): void => {
        const clickTargetDetails = item.split(' | ');
        const targetRating = clickTargetDetails[1];
        const targetDate = clickTargetDetails[0];

        if (targetRating && targetDate && upcomingAssessmentsChartData?.assessmentSubjectsData) {
            setAssessmentSubjectsModalSubtitle(`${targetRating} - ${targetDate}`);
            setModalState(Modals.DISPLAY_ASSESSMENT_SUBJECTS);
            const selectedAssessmentSubjects = upcomingAssessmentsChartData.assessmentSubjectsData
                .filter((assessmentSubject) => {
                    if (assessmentSubject.monthKey.toUpperCase() === targetDate) {
                        if (riskType === DueDiligenceDashboardRiskType.CONTROL_EFFECTIVENESS) {
                            if (assessmentSubject.riskScore !== undefined && effectivenessAsString(assessmentSubject.riskScore) === targetRating) {
                                return assessmentSubject;
                            } else {
                                return null;
                            }
                        } else if (assessmentSubject.riskScore !== undefined && riskRatingAsString(assessmentSubject.riskScore) === targetRating) {
                            return assessmentSubject;
                        } else {
                            return null;
                        }
                    } else {
                        return null;
                    }
                })
                .map((assessmentSubject) => ({
                    assessmentSubjectId: assessmentSubject.assessmentSubjectId,
                    assessmentSubjectName: assessmentSubject.assessmentSubjectName,
                    organizationId: assessmentSubject.organizationId,
                    organizationName: assessmentSubject.organizationName,
                }));
            setSelectedAssessmentSubjects(selectedAssessmentSubjects);
        }
    };

    const handleAssessmentSubjectsModalClose = () => {
        setModalState(Modals.NONE);
        setAssessmentSubjectsModalSubtitle(undefined);
        setSelectedAssessmentSubjects(undefined);
    };

    const handleRiskTypeChange = (value: ChangeEventType) => {
        if (value && isValidDashboardRiskType(value)) {
            setRiskType(value);
        }
    };

    if (props.assessmentSubjectsError) {
        return (
            <>
                <div className={styles.cellHeader}>
                    <Text variant="Header2" color="white">
                        Upcoming Due Diligence
                    </Text>
                </div>
                <Text color="white">{props.assessmentSubjectsError}</Text>
            </>
        );
    }

    return (
        <>
            {modalState === Modals.CUSTOM_DATE && <CustomDateRangeModal datesRestriction="futureDatesOnly" hideModal={() => setModalState(Modals.NONE)} dateFilterHookValues={dateFilterHookValues} />}
            {modalState === Modals.DISPLAY_ASSESSMENT_SUBJECTS && assessmentSubjectsModalSubtitle && selectedAssessmentSubjects && <AssessmentSubjectsDisplayModal hideModal={handleAssessmentSubjectsModalClose} module={props.module} subtitle={assessmentSubjectsModalSubtitle} selectedAssessmentSubjects={selectedAssessmentSubjects} />}
            <div className={styles.cellHeader}>
                <Text variant="Header2" color="white">
                    Upcoming Due Diligence
                </Text>
                <div className={styles.filters}>
                    <FormFieldSelectDark formFieldId="riskType" formFieldLabel="Risk Type" options={DashboardRiskOptions} selectedOption={riskType} handleChange={handleRiskTypeChange} />
                    <FormFieldDateOptionSelect formFieldId="date" options={DefaultUpcomingDateRangeOptions} onSelectCustom={() => setModalState(Modals.CUSTOM_DATE)} dateFilterHookValues={dateFilterHookValues} />
                </div>
            </div>
            {upcomingAssessmentsChartData ? <GroupedBarsChart groupingMode="stacked" onChartClick={handleChartClick} xAxisLabels={upcomingAssessmentsChartData.xAxisLabels} barsSeriesArray={upcomingAssessmentsChartData.assessmentDataRows} yAxisInfo={upcomingAssessmentsChartData.yAxisInfo} /> : <Skeleton variant="rounded" width={'100%'} height={300} />}{' '}
        </>
    );
};
